import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Gauge, Target, Plus, Trash2, Lightbulb, CalendarDays, Trophy } from "lucide-react";

const RANGES = [
  { min: 300, max: 579, label: "Poor", color: "text-rose-400", bar: "bg-rose-500", ring: "border-rose-500/40 bg-rose-500/10 text-rose-300", dot: "#fb7185" },
  { min: 580, max: 669, label: "Fair", color: "text-orange-400", bar: "bg-orange-500", ring: "border-orange-500/40 bg-orange-500/10 text-orange-300", dot: "#fb923c" },
  { min: 670, max: 739, label: "Good", color: "text-yellow-400", bar: "bg-yellow-500", ring: "border-yellow-500/40 bg-yellow-500/10 text-yellow-300", dot: "#facc15" },
  { min: 740, max: 799, label: "Very Good", color: "text-emerald-400", bar: "bg-emerald-500", ring: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", dot: "#34d399" },
  { min: 800, max: 850, label: "Exceptional", color: "text-emerald-300", bar: "bg-emerald-400", ring: "border-emerald-400/40 bg-emerald-400/10 text-emerald-200", dot: "#6ee7b7" },
];
const rangeOf = (s) => RANGES.find((r) => s >= r.min && s <= r.max) || RANGES[0];

const TIPS = {
  Poor: [
    "Pay every bill on time — payment history is 35% of your score.",
    "Bring past-due accounts current, then call lenders about hardship plans.",
    "Keep your oldest accounts open — length of history helps you recover.",
    "Become an authorized user on a family member's healthy, old card.",
  ],
  Fair: [
    "Pay down revolving balances below 30% of each card's limit.",
    "Avoid opening several new accounts at once — each hard pull dings you.",
    "Set up autopay for at least the minimum so you never miss a due date.",
    "Dispute any report errors at AnnualCreditReport.com — mistakes are common.",
  ],
  Good: [
    "Push card utilization under 10% for a quick score bump.",
    "Ask for a credit-limit increase (without spending more) to lower utilization.",
    "Keep your oldest card active with a small recurring charge.",
    "Pay the balance before the statement closes, not just before the due date.",
  ],
  "Very Good": [
    "Spread balances so no single card sits near its limit.",
    "Limit hard inquiries — only apply for credit you truly need.",
    "Maintain a healthy mix of credit types (cards, loans) over time.",
    "Let your average account age keep climbing — patience pays here.",
  ],
  Exceptional: [
    "You're in the top tier — keep utilization low and payments on time.",
    "Review your reports yearly for errors that could cost you.",
    "Leverage your score for the best rates when refinancing or borrowing.",
    "Don't close old cards — they anchor your long credit history.",
  ],
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const dayOfYear = () => { const d = new Date(); const s = new Date(d.getFullYear(), 0, 0); return Math.floor((d - s) / 86400000); };

export default function CreditScoreTracker() {
  const [entries, setEntries] = useState([]);
  const [goal, setGoal] = useState(null);
  const [goalInput, setGoalInput] = useState("");
  const [scoreInput, setScoreInput] = useState("");
  const [dateInput, setDateInput] = useState(todayStr());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/credit-score").then((r) => {
      setEntries(r.data.entries || []);
      setGoal(r.data.goal ?? null);
      setGoalInput(r.data.goal ? String(r.data.goal) : "");
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || "")), [entries]);
  const current = sorted.length ? sorted[sorted.length - 1].score : null;
  const prev = sorted.length > 1 ? sorted[sorted.length - 2].score : null;
  const delta = current != null && prev != null ? current - prev : null;
  const range = current != null ? rangeOf(current) : null;

  const logScore = async () => {
    const s = parseInt(scoreInput, 10);
    if (!s || s < 300 || s > 850) { toast.error("Enter a score between 300 and 850"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/credit-score", { score: s, date: dateInput || todayStr() });
      setEntries(data.entries || []);
      setGoal(data.goal ?? null);
      setScoreInput("");
      toast.success("Score logged");
    } catch { toast.error("Could not save score"); } finally { setBusy(false); }
  };

  const saveGoal = async () => {
    const g = goalInput === "" ? null : Math.max(300, Math.min(850, parseInt(goalInput, 10) || 0));
    setGoal(g);
    setGoalInput(g ? String(g) : "");
    api.put("/credit-score/goal", { goal: g }).catch(() => toast.error("Could not save goal"));
  };

  const removeEntry = async (id) => {
    try { const { data } = await api.delete(`/credit-score/${id}`); setEntries(data.entries || []); } catch { /* ignore */ }
  };

  // Chart geometry
  const scores = sorted.map((e) => e.score);
  const allVals = [...scores, ...(goal ? [goal] : [])];
  let lo = allVals.length ? Math.min(...allVals) : 300;
  let hi = allVals.length ? Math.max(...allVals) : 850;
  lo = Math.max(300, lo - 20); hi = Math.min(850, hi + 20);
  if (hi - lo < 60) hi = Math.min(850, lo + 60);
  const rng = (hi - lo) || 1;
  const W = 600, H = 150, pad = 18;
  const pts = sorted.map((e, i) => {
    const x = pad + i * ((W - 2 * pad) / Math.max(1, sorted.length - 1));
    const y = H - pad - ((e.score - lo) / rng) * (H - 2 * pad);
    return [x, y];
  });
  const path = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = pts.length > 1 ? `${path} L${pts[pts.length - 1][0].toFixed(1)},${H - pad} L${pts[0][0].toFixed(1)},${H - pad} Z` : "";
  const goalY = goal ? H - pad - ((Math.max(lo, Math.min(hi, goal)) - lo) / rng) * (H - 2 * pad) : null;

  // Goal progress (from 300 baseline toward goal)
  const pct = goal && current != null && goal > 300 ? Math.min(100, Math.max(0, Math.round(((current - 300) / (goal - 300)) * 100))) : 0;
  const reachedGoal = goal != null && current != null && current >= goal;
  const gapToGoal = goal != null && current != null ? goal - current : null;
  const nearGoal = gapToGoal != null && gapToGoal > 0 && gapToGoal <= 20;

  // Rotating coaching tips
  const tips = range ? TIPS[range.label] : [];
  const start = tips.length ? dayOfYear() % tips.length : 0;
  const shownTips = tips.length ? [0, 1, 2].map((i) => tips[(start + i) % tips.length]) : [];

  return (
    <section data-testid="snap-credit-tracker" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <Gauge className="w-5 h-5" /> Credit Score Tracker
        </h2>
        <span className="text-[11px] text-slate-500">Self-reported</span>
      </div>

      {/* Current score + range */}
      <div className="flex flex-wrap items-end gap-4 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Current score</p>
          <div className="flex items-end gap-3">
            <span data-testid="credit-current-score" className={`font-mono text-5xl font-bold leading-none ${range ? range.color : "text-slate-400"}`}>{current != null ? current : "—"}</span>
            {delta != null && delta !== 0 && (
              <span className={`text-sm font-mono mb-1 ${delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>{delta > 0 ? "+" : ""}{delta}</span>
            )}
          </div>
        </div>
        {range && (
          <span data-testid="credit-range-label" className={`inline-flex items-center h-7 px-3 rounded-full text-xs font-semibold border ${range.ring}`}>{range.label}</span>
        )}
      </div>

      {/* Log input */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[120px]">
          <label className="text-[11px] text-slate-400 flex items-center gap-1 mb-1"><Gauge className="w-3 h-3" /> Today's score</label>
          <input
            type="number" min="300" max="850" value={scoreInput} onChange={(e) => setScoreInput(e.target.value)}
            placeholder="e.g. 712" data-testid="credit-score-input"
            className="w-full h-10 px-3 rounded-lg bg-slate-900/70 border border-white/10 font-mono text-emerald-100 focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="flex-1 min-w-[130px]">
          <label className="text-[11px] text-slate-400 flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" /> Date</label>
          <input
            type="date" value={dateInput} max={todayStr()} onChange={(e) => setDateInput(e.target.value)}
            data-testid="credit-date-input"
            className="w-full h-10 px-3 rounded-lg bg-slate-900/70 border border-white/10 text-emerald-100 focus:outline-none focus:border-emerald-500 [color-scheme:dark]"
          />
        </div>
        <button
          data-testid="credit-log-btn" onClick={logScore} disabled={busy}
          className="h-10 px-4 rounded-lg bg-emerald-500 text-[#04120c] text-sm font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-50 flex items-center gap-1.5 shrink-0"
        >
          <Plus className="w-4 h-4" /> Log score
        </button>
      </div>
      <p className="text-[11px] text-slate-500 -mt-3 mb-5">Grab your latest number from Credit Karma, your bank app, or a lender statement — nothing is pulled automatically.</p>

      {entries.length === 0 && (
        <div data-testid="credit-karma-tip" className="rounded-xl border border-sky-500/25 bg-sky-500/[0.06] p-4 mb-5">
          <p className="text-xs font-semibold text-sky-300 flex items-center gap-1.5 mb-2"><Lightbulb className="w-4 h-4" /> Where to find your free score</p>
          <p className="text-sm text-slate-300">Not sure where to get your free credit score? Try Credit Karma — it is free, requires no credit card, and updates weekly. Experian and most major bank apps also offer your score for free.</p>
          <p className="mt-2 text-[11px] font-semibold text-sky-200">Recommended: Credit Karma</p>
        </div>
      )}

      {/* Goal + progress */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-5">
        <div className="flex items-center gap-2 mb-2 text-xs">
          <Target className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <span className="text-slate-400 whitespace-nowrap">Goal score</span>
          <input
            type="number" min="300" max="850" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} onBlur={saveGoal}
            placeholder="e.g. 760" data-testid="credit-goal-input"
            className="ml-auto w-24 h-8 px-2 rounded bg-slate-900/70 border border-white/10 text-right font-mono text-emerald-200 focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="h-3 bg-white/[0.05] rounded-full overflow-hidden mb-1">
          <div data-testid="credit-goal-progress" className={`h-full rounded-full transition-all duration-500 ${reachedGoal ? "bg-emerald-400" : "bg-emerald-500/70"}`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[11px] text-slate-500">
          {!goal ? "Set a target score to track your climb."
            : current == null ? `Log a score to measure progress toward ${goal}.`
            : reachedGoal ? `Goal reached! You're at ${current} (target ${goal}).`
            : `${goal - current} point${goal - current === 1 ? "" : "s"} to your ${goal} goal.`}
        </p>
      </div>

      {reachedGoal && (
        <div data-testid="credit-goal-reached" className="credit-shimmer mb-5 flex items-center gap-3 rounded-xl border border-emerald-400/50 px-4 py-3 shadow-[0_0_22px_rgba(52,211,153,0.25)]">
          <Trophy className="w-5 h-5 text-amber-300 shrink-0" />
          <p className="text-sm font-medium text-emerald-50">Goal smashed! You hit your <span className="font-mono font-bold text-amber-300">{goal}</span> target — now sitting at {current}. 🎯</p>
        </div>
      )}

      {nearGoal && (
        <div data-testid="credit-goal-nudge" className="mb-5 flex items-center gap-3 rounded-xl border border-amber-400/40 bg-amber-400/[0.07] px-4 py-3">
          <Target className="w-5 h-5 text-amber-300 shrink-0" />
          <p className="text-sm text-amber-100">Almost there — you're <span className="font-mono font-bold text-amber-300">{gapToGoal}</span> point{gapToGoal === 1 ? "" : "s"} from your {goal} goal. Keep the momentum going!</p>
        </div>
      )}

      {/* History chart */}
      <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Score history</p>
      {sorted.length === 0 ? (
        <div data-testid="credit-empty" className="h-32 grid place-items-center text-sm text-slate-500 border border-dashed border-white/10 rounded-xl mb-5">
          No scores logged yet — add your first above.
        </div>
      ) : (
        <div className="mb-5">
          <svg data-testid="credit-chart" viewBox={`0 0 ${W} ${H}`} className="w-full h-36" preserveAspectRatio="none">
            {goalY != null && (
              <line x1={pad} y1={goalY} x2={W - pad} y2={goalY} stroke="#facc15" strokeWidth="1.5" strokeDasharray="5 4" opacity="0.7" />
            )}
            {area && <path d={area} fill="rgba(16,185,129,0.10)" />}
            <path d={path} fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            {pts.map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill={rangeOf(sorted[i].score).dot} />
            ))}
          </svg>
          <div className="space-y-1 mt-3 max-h-32 overflow-y-auto scroll-thin pr-1" data-testid="credit-entries">
            {[...sorted].reverse().slice(0, 8).map((e) => {
              const r = rangeOf(e.score);
              return (
                <div key={e.id} data-testid={`credit-entry-${e.id}`} className="flex items-center gap-3 text-xs">
                  <span className="w-24 shrink-0 text-slate-500 font-mono">{e.date}</span>
                  <span className={`font-mono font-bold ${r.color}`}>{e.score}</span>
                  <span className="text-slate-500">{r.label}</span>
                  <button data-testid={`credit-delete-${e.id}`} onClick={() => removeEntry(e.id)} title="Delete entry" className="ml-auto text-slate-600 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rotating coaching tips */}
      {range && (
        <div data-testid="credit-tips" className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
          <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5 mb-3">
            <Lightbulb className="w-4 h-4" /> Coaching for a "{range.label}" score
          </p>
          <ul className="space-y-2">
            {shownTips.map((tip, i) => (
              <li key={i} data-testid={`credit-tip-${i}`} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" /> {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
